import React, { useEffect, useMemo, useState } from 'react';
import db from '../db/localdb';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/date';
import BackButton from '../components/BackButton';
import { useToast } from '../ToastContext';
import * as repo from '../sync/repo';
import { purchaseAPI } from '../services/api';

// Ask why before voiding. Returns null if cancelled.
const askReason = (what) => {
  const r = window.prompt(`Why are you voiding this ${what}? (e.g. entered twice, wrong product)`);
  return r === null ? null : (r.trim() || 'Voided');
};

// Local (not UTC) YYYY-MM-DD for the <input type="date"> defaults.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function Transactions() {
  const { business, isManager, hasPermission, readOnly } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const [tab, setTab]   = useState('sales');   // 'sales' | 'purchases'
  const [from, setFrom] = useState(() => todayISO());
  const [to, setTo]     = useState(() => todayISO());
  const [sales, setSales]         = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts]   = useState({});
  const [loading, setLoading]     = useState(true);

  // Read from the local mirror so the history works offline.
  useEffect(() => {
    const bid = business?.id;
    if (!bid) { setLoading(false); return; }
    let alive = true;
    (async () => {
      const [sr, pu, pr] = await Promise.all([
        db.sales.where('business_id').equals(bid).filter(s => !s.voided_at).toArray(),
        db.purchases.where('business_id').equals(bid).filter(p => !p.voided_at).toArray(),
        db.products.where('business_id').equals(bid).toArray(),
      ]);
      if (!alive) return;
      const pmap = {}; pr.forEach(p => { pmap[p.id] = p.name; });
      setSales(sr); setPurchases(pu); setProducts(pmap);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [business?.id]);

  const cur  = business?.currency || '';
  const fmt  = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const fmtQ = (n) => parseFloat(Number(n || 0).toFixed(3));

  const invalid = from && to && from > to;

  // Inclusive [from 00:00 … to 23:59:59.999] window.
  const inRange = useMemo(() => {
    const startMs = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const endMs   = to   ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
    return (dateStr) => {
      const t = new Date(dateStr).getTime();
      return t >= startMs && t <= endMs;
    };
  }, [from, to]);

  const rows = useMemo(() => {
    if (invalid) return [];
    if (tab === 'sales') {
      return sales.filter(s => inRange(s.sold_at)).sort((a, b) => new Date(b.sold_at) - new Date(a.sold_at));
    }
    return purchases.filter(p => inRange(p.purchased_at)).sort((a, b) => new Date(b.purchased_at) - new Date(a.purchased_at));
  }, [tab, sales, purchases, inRange, invalid]);

  const total = useMemo(() => {
    if (tab === 'sales') return rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    return rows.reduce((s, r) => s + Number(r.qty || 0) * Number(r.unit_cost || 0), 0);
  }, [rows, tab]);

  // A mistake can be reversed: voiding keeps the record (with who and why)
  // but removes it from stock, revenue and cash.
  const voidSale = async (s) => {
    const reason = askReason('sale');
    if (reason === null) return;
    setBusy(s.id);
    try {
      const r = await repo.voidSale(s.id, reason);
      setSales(list => list.filter(x => x.id !== s.id));
      toast(r._offline ? 'Void queued — it will sync when you are back online' : 'Sale voided', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Could not void the sale', 'error');
    } finally { setBusy(null); }
  };

  const voidPurchase = async (p) => {
    const reason = askReason('purchase');
    if (reason === null) return;
    setBusy(p.id);
    try {
      await purchaseAPI.void(p.id, { reason });
      await db.purchases.where('id').equals(p.id).modify({ voided_at: new Date().toISOString() });
      setPurchases(list => list.filter(x => x.id !== p.id));
      toast('Purchase voided', 'success');
    } catch (err) {
      toast(err.response ? (err.response.data?.error || 'Could not void the purchase') : 'You need to be online to void a purchase', 'error');
    } finally { setBusy(null); }
  };

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  const TabBtn = (k, l) => (
    <button key={k} onClick={() => setTab(k)} className={tab === k ? 'btn-primary' : 'btn-ghost'}
      style={{ flex: 1, padding: '0.5rem' }}>{l}</button>
  );

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Transactions</h1>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {TabBtn('sales', 'Sales')}
        {TabBtn('purchases', 'Purchases')}
      </div>

      {/* Date range */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>From</label>
          <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>To</label>
          <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button type="button" className="btn-ghost" style={{ flex: 1, padding: '0.35rem', fontSize: '0.78rem' }}
          onClick={() => { setFrom(todayISO()); setTo(todayISO()); }}>Today</button>
        <button type="button" className="btn-ghost" style={{ flex: 1, padding: '0.35rem', fontSize: '0.78rem' }}
          onClick={() => { const d = new Date(); d.setDate(d.getDate() - 6); const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; setFrom(iso); setTo(todayISO()); }}>Last 7 days</button>
        <button type="button" className="btn-ghost" style={{ flex: 1, padding: '0.35rem', fontSize: '0.78rem' }}
          onClick={() => { const d = new Date(); const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; setFrom(iso); setTo(todayISO()); }}>This month</button>
      </div>

      {invalid && <p style={{ color: 'var(--color-danger)', fontSize: '0.82rem' }}>“From” date must be on or before “To” date.</p>}

      <div className="card" style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
          {tab === 'sales' ? 'Total sales' : 'Total restocking cost'} · {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
        </div>
        <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{cur} {fmt(total)}</div>
      </div>

      {!invalid && rows.length === 0 && <p className="text-muted">No {tab === 'sales' ? 'sales' : 'purchases'} in this range.</p>}

      {tab === 'sales' && rows.map(s => (
        <div key={s.id} className="card" style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span style={{ fontWeight: 600 }}>{products[s.product_id] || 'Product'}</span>
            <span style={{ fontWeight: 700, flexShrink: 0 }}>{cur} {fmt(s.total_amount)}</span>
          </div>
          <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: '0.2rem' }}>
            {s.buyer_name || 'Walk-in'} · Qty {fmtQ(s.qty)} · {fmtDate(s.sold_at)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem' }}>
            <span className={`badge badge-${s.payment_status}`} style={{ fontSize: '0.68rem' }}>{s.payment_status}</span>
            {isManager && !readOnly && (
              <button className="btn-ghost" style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem' }}
                disabled={busy === s.id} onClick={() => voidSale(s)}>
                {busy === s.id ? 'Voiding…' : 'Void'}
              </button>
            )}
          </div>
        </div>
      ))}

      {tab === 'purchases' && rows.map(p => (
        <div key={p.id} className="card" style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span style={{ fontWeight: 600 }}>{products[p.product_id] || 'Product'}</span>
            <span style={{ fontWeight: 700, flexShrink: 0 }}>{cur} {fmt(Number(p.qty) * Number(p.unit_cost))}</span>
          </div>
          <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: '0.2rem' }}>
            Qty {fmtQ(p.qty)} · {cur} {fmt(p.unit_cost)}/unit · {fmtDate(p.purchased_at)}
          </div>
          {p.supplier && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{p.supplier}</div>}
          {hasPermission('manage_purchases') && !readOnly && (
            <div style={{ textAlign: 'right' }}>
              <button className="btn-ghost" style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem' }}
                disabled={busy === p.id} onClick={() => voidPurchase(p)}>
                {busy === p.id ? 'Voiding…' : 'Void'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
